import { GridLayoutElement, GridLayouts } from 'adapter/in/dashboards/components/generic/ReactGrid';
import { ResponsiveReactGridLayoutProps } from 'adapter/in/dashboards/components/generic/ReactGrid/ResponsiveReactGridLayout';
import { LayoutType } from 'domain/entity/layout';

// Returns the breakpoint that ReactGridLayout is currently using.
export const getCurrentBreakpoint = (layouts: GridLayouts, passedProps: any): string => passedProps?.breakpoint || Object.keys(layouts)[0];

// Determine whether the current breakpoint is A4.
export const isA4Breakpoint = (bp: string): boolean => bp === LayoutType.A4;

// Computes the amount of rows that the layout occupies vertically.
export const computeLayoutRows = (layout: GridLayoutElement[]): number =>
    layout.length === 0 ? 0 : Math.max(...layout.map((item) => item.y + item.h));

// Returns the vertical margin between rows (in pixels).
export const getMarginY = (margin: ResponsiveReactGridLayoutProps['margin']): number => {
    if (typeof margin === 'number') return margin;
    if (Array.isArray(margin)) return margin[1];
    return 0;
};

// Computes the pixel positions of page breaks.
export const computePageBreakPositions = (totalPages: number, rowPx: number, rowsPerPage: number): number[] =>
    Array.from({ length: totalPages }).map((_, i) => (i + 1) * rowsPerPage * rowPx);

// Detects layouts that would overflow horizontally (e.g. layouts carried over from a
// breakpoint with a different amount of columns, like DESKTOP layouts reused on TABLET/MOBILE).
export const doesLayoutOverflowColumns = (layout: GridLayoutElement[], cols: number): boolean =>
    layout.some((element) => element.x < 0 || element.w > cols || element.x + element.w > cols);

// Reflows a layout into a single, full-width column so widgets are stacked vertically
// (in reading order) instead of requiring horizontal scrolling to be fully visible.
export const reflowLayoutToSingleColumn = (layout: GridLayoutElement[], cols: number): GridLayoutElement[] => {
    const sortedByReadingOrder = [...layout].sort((a, b) => (a.y === b.y ? a.x - b.x : a.y - b.y));

    let nextY = 0;
    return sortedByReadingOrder.map((element) => {
        const reflowedElement = { ...element, x: 0, y: nextY, w: cols };
        nextY += element.h;
        return reflowedElement;
    });
};

// Ensures a layout fits within the given amount of columns, reflowing it into a single
// column stack when it doesn't (and leaving intentionally-designed layouts untouched).
export const getOverflowSafeLayout = (layout: GridLayoutElement[], cols: number): GridLayoutElement[] =>
    doesLayoutOverflowColumns(layout, cols) ? reflowLayoutToSingleColumn(layout, cols) : layout;
